import {
   BLUE_GREY_200,
   Callout,
   GREEN_400,
   Typography,
   YELLOW_500,
   Loader,
} from '@airtel/air-design-system';
import { ButtonWidget } from '@airtel/air-widgets-library';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-native';
import { getInteractionId, generateUniqueId } from '../../common/commonUtils';
import { closeCameraModalWithErrorAction } from '../../common/userImageUploadUtils';
import CameraCircleOverlay from './camera-circle-overlay';
import VideoRecordingButton from './video-recording-button';
import { ChildComponentHandles, VideoCam } from './VideoCam';
import { VideoModalPropsTypes } from './VideoModalPropTypes';
import './VideoModalStyles.scss';
import VideoReplay from './VideoReplay';
import VideoTimer from './VideoTimer';
import ModalCloseButton from '../modal-close-button';

const VideoModal: FC<VideoModalPropsTypes> = (props) => {
   const { visible, userLocation = {}, artistRef, getReduxState, isUploaded, setIsVideoModalOpen } = props;
   let { latitude = '', longitude = '' } = userLocation;

   const webcamRef = useRef<ChildComponentHandles>(null);
   const mediaRecorderRef = useRef<MediaRecorder | null>(null);
   const recordedChunksRef = useRef<Blob[]>([]);
   const [capturing, setCapturing] = useState(false);
   const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
   const videoRecInterval = useRef<ReturnType<typeof setTimeout> | null>(null);
   const [duration, setDuration] = useState<number>(0);
   const [progressColor, setProgressColor] = useState<string>(YELLOW_500);
   const [videoProgress, setVideoProgress] = useState<number>(0);
   const [previewVideo, setPreviewVideo] = useState<boolean>(false);
   const [videoURL, setVideoURL] = useState<string>('');
   const [videoData, setVideoData] = useState<Blob | null>(null);
   const [userDetails, setUserDetails] = useState({
      aadhaarName: '',
      dateOfBirth: '',
   });
   const [loader, setLoader] = useState(true);

   useEffect(() => {
      const monthNames = [
         'January',
         'February',
         'March',
         'April',
         'May',
         'June',
         'July',
         'August',
         'September',
         'October',
         'November',
         'December',
      ];
      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';

      const { aadhaarName, dob } = storeValues?.kycDetails ?? {};
      const userDOB = dob?.split('-')?.map(Number) ?? [];
      let day, month, year;

       // Dynamically determine the date format
      if (userDOB[0] > 31) {
         // Format is YYYY-MM-DD
         [year, month, day] = userDOB;
      } else {
         // Format is DD-MM-YYYY
         [day, month, year] = userDOB;
      }

      const dateOfBirth =
         month && day && year ? new Date(year, month - 1, day) : new Date();
      const dd =
         dateOfBirth.getDate() < 10
            ? '0' + dateOfBirth?.getDate()
            : dateOfBirth?.getDate();
      const mm = monthNames[dateOfBirth?.getMonth()];
      const yy = dateOfBirth?.getFullYear();

      setUserDetails({ aadhaarName, dateOfBirth: `${dd} ${mm} ${yy}` });

      const actions = [];

      if (isNewSKYCJourney) {
         actions?.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
               section: 'stepsSection',
               subSection: 'Step5Widget',
               eventAction: 'impression',
               eventLabel: 'read',
               eventValue: `I am ${aadhaarName}, born on ${dd} ${mm} ${yy}`,
               customValue: '',
               customLabel: 'card',
               prop35: '{{storeValues:orderData.orderData.cartId}}',
               extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
               prop45: '{{storeValues:appData.subJourney}}',
               eVar21: '{{storeValues:appData.loggedInNumber}}',
               eVar36: '{{storeValues:appData.msisdn}}',
               city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
               eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
               prop4: 'SKYC',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop7: '{{storeValues:outstationContext.isOutstation}}',
               prop9: '{{storeValues:appData.lob}}'
            },
         });
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE',
               section: 'stepssection',
               subSection: 'Step5Widget',
               eventAction: 'impression',
               eventLabel: 'camera prompt',
               eventValue: `Read - I am ${aadhaarName}, born on ${dd} ${mm} ${yy}`,
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }
      artistRef?.current?.onUserAction({
         actions,
      });
   }, [getReduxState?.()]);

   useEffect(() => {
      if (capturing) {
         videoRecInterval.current = setInterval(() => {
            setVideoProgress((prevProg) => prevProg + 20);
            setDuration((prevDuration) => {
               const newDuration = prevDuration + 1;
               if (newDuration >= 4) {
                  setProgressColor(GREEN_400);
               }
               if (newDuration === 10) {
                  handleStopCaptureClick();
               }
               return newDuration;
            });
         }, 1000);
      } else {
         if (videoRecInterval.current) {
            clearInterval(videoRecInterval.current);
            videoRecInterval.current = null;
         }
      }
      return () => {
         if (videoRecInterval.current) {
            clearInterval(videoRecInterval.current);
            setProgressColor(YELLOW_500);
         }
      };
   }, [capturing]);

   const resetVideoData = () => {
      setRecordedChunks([]);
      recordedChunksRef.current = [];
      setVideoData(null);
      setVideoURL('');
      setDuration(0);
      setVideoProgress(0);
      setProgressColor(YELLOW_500);
      setPreviewVideo(false);
   };

   // ✅ NEW: Wait for video track to actually be producing frames
   const waitForActiveVideoTrack = async (
      getStream: () => MediaStream | undefined | null,
      maxWaitMs = 3000
   ): Promise<MediaStream | null> => {
      const start = Date.now();
      while (Date.now() - start < maxWaitMs) {
         const stream = getStream();
         const videoTrack = stream?.getVideoTracks?.()[0];
         if (
            stream &&
            videoTrack &&
            videoTrack.readyState === 'live' &&
            videoTrack.enabled &&
            !videoTrack.muted
         ) {
            return stream;
         }
         await new Promise((r) => setTimeout(r, 100));
      }
      return null;
   };

   const handleVideoCapture = async () => {
      // setCapturing(true);
      resetVideoData();
      // setTimeout(() => {
      //    let stream = webcamRef.current?.getMediaStream?.();
      //    const options: MediaRecorderOptions = {
      //       mimeType: 'video/mp4',
      //       videoBitsPerSecond: 1200000
      //    };
      //    mediaRecorderRef.current = new MediaRecorder(stream as MediaStream, options);
      //    mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);
      //    mediaRecorderRef.current.start();
      // }, 500);


       // ✅ Wait until camera stream is truly producing frames (max 3s)
       const stream = await waitForActiveVideoTrack(
         () => webcamRef.current?.getMediaStream?.()
      );
      if (!stream) {
         closeVideoModalWithError();
         return;
      }

      try {
         const options: MediaRecorderOptions = {
            mimeType: 'video/mp4',
            videoBitsPerSecond: 800000, // ⬇️ safer than 1.2M for weak devices
         };
         const recorder = new MediaRecorder(stream, options);
         recorder.addEventListener('dataavailable', handleDataAvailable);

         // ✅ Fail loudly if recorder errors mid-recording (was silent before)
         recorder.addEventListener('error', (e) => {
            console.error('MediaRecorder error:', e);
            closeVideoModalWithError();
         });

         // ✅ Detect if camera is stolen by call / OS / backgrounding
         const videoTrack = stream.getVideoTracks()[0];
         const onTrackMute = () => {
            console.warn('Video track muted mid-recording — stopping');
            handleStopCaptureClick();
         };
         videoTrack?.addEventListener('mute', onTrackMute);
         recorder.addEventListener('stop', () => {
            videoTrack?.removeEventListener('mute', onTrackMute);
         });

         mediaRecorderRef.current = recorder;

         // ✅ Start recorder FIRST, then flip capturing so UI timer syncs with real recording
         recorder.start(1000); // 👈 timeslice = 1s → periodic dataavailable → detect failure fast
         setCapturing(true);
      } catch (err) {
         console.error('Failed to start MediaRecorder:', err);
         closeVideoModalWithError();
         return;
      }



      const actions = [];
      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      const durationString = duration?.toString().padStart(2, '0');
      const isPrepaidSimLayout = storeValues?.journeyContext?.layout === 'prepaid_sim_layout'

      if (!isPrepaidSimLayout) {
         if (isNewSKYCJourney) {
            actions?.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                  section: 'stepsSection',
                  subSection: 'Step5Widget',
                  eventAction: 'click',
                  eventLabel: '',
                  eventValue: 'position your face in the centre',
                  customValue: `${durationString} SEC`,
                  customLabel: 'button',
                  prop35: '{{storeValues:orderData.orderData.cartId}}',
                  extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                  prop45: '{{storeValues:appData.subJourney}}',
                  eVar21: '{{storeValues:appData.loggedInNumber}}',
                  eVar36: '{{storeValues:appData.msisdn}}',
                  city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                  eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                  prop4: 'SKYC',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop7: '{{storeValues:outstationContext.isOutstation}}',
                  prop9: '{{storeValues:appData.lob}}'
               },
            });
         } else {
            actions.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'SKYC_LANDING_PAGE',
                  section: 'stepssection',
                  subSection: 'Step5Widget',
                  eventAction: 'click',
                  eventLabel: 'capture video',
                  eVar21: '{{storeValues:appData.msisdn}}',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop8: 'reverification',
                  prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
                  prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
                  prop5: '{{storeValues:outstationContext.isOutstation}}',
               },
            });
         }
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: "new_acq_postpaid_skyc",
               section: "stepsSection",
               subSection: "Step5Widget",
               eVar33: "{{storeValues:pincodeContext.pincodeDetails.pincode}}",
               eVar21: "{{storeValues:appData.msisdn}}",
               eVar20: "{{storeValues:appData.loggedInNumber}}",
               city: "{{storeValues:pincodeContext.pincodeDetails.city}}",
               customValue: "",
               eVar36: "{{storeValues:trackOrderContext.selectedMsisdn}}",
               eVar25: "{{storeValues:pincodeContext.pincodeDetails.circleId}}",
               isInteractive: 1,
               cartId: "{{storeValues:orderData.cartItemId}}",
               eventAction: "click",
               eventLabel: "capture video",
               prop5: "{{storeValues:planInfo.planName}}",
               prop4: "SKYC",
               eventValue: "video verification",
               eVar0: "skyc_click on video capture",
               customLabel: "button",
               prop45: "{{storeValues:appData.orderType}}",
               ticketId: "{{storeValues:orderData.cartId}}",
               prop26: ""
            },
         });
      }
      artistRef?.current?.onUserAction({
         actions,
      });
   };

   const handleDataAvailable = ({ data }: BlobEvent) => {
      if (data.size > 0) {
         if (isUploaded) isUploaded.current = false;
   
         recordedChunksRef.current.push(data);
         setRecordedChunks([...recordedChunksRef.current]);
      }
   };

   const handleStopCaptureClick = () => {
      const recorder = mediaRecorderRef.current;

      if (!recorder || recorder.state === 'inactive') {
         return;
      }

      recorder.addEventListener(
         'stop',
         () => {
            prepareVideo(recordedChunksRef.current);

            setCapturing(false);
            setVideoProgress(0);
            setPreviewVideo(true);
            mediaRecorderRef.current = null;
         },
         { once: true }
      );

      recorder.stop();
   };

   const prepareVideo = useCallback(
      (completeRecorderedChecks = recordedChunks) => {
         if (completeRecorderedChecks.length) {
            const blob = new Blob(completeRecorderedChecks, {
               type: 'video/mp4',
            });
            setVideoData(blob);
            var reader = new FileReader();
            const url = URL.createObjectURL(blob);
            reader.readAsDataURL(blob);
            reader.onloadend = function () {
               var base64data = reader.result;
               setVideoURL(base64data as string);
            };
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.href = url;
            window.URL.revokeObjectURL(url);
            // setRecordedChunks([]);
         }
      },
      [recordedChunks]
   );

   const onPressRetry = () => {
      resetVideoData();
      const actions = [];
      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      const isPrepaidSimLayout = storeValues?.journeyContext?.layout === 'prepaid_sim_layout'

      if (!isPrepaidSimLayout) {
         if (isNewSKYCJourney) {
            actions?.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                  section: 'stepsSection',
                  subSection: 'Step5Widget',
                  eventAction: 'click',
                  eventLabel: 'retry',
                  eventValue: '',
                  customValue: '',
                  customLabel: 'button',
                  prop35: '{{storeValues:orderData.orderData.cartId}}',
                  extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                  prop45: '{{storeValues:appData.subJourney}}',
                  eVar21: '{{storeValues:appData.loggedInNumber}}',
                  eVar36: '{{storeValues:appData.msisdn}}',
                  city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                  eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                  prop4: 'SKYC',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop7: '{{storeValues:outstationContext.isOutstation}}',
                  prop9: '{{storeValues:appData.lob}}'
               },
            });
         } else {
            actions.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'SKYC_LANDING_PAGE',
                  section: 'stepssection',
                  subSection: 'Step5Widget',
                  eventAction: 'click',
                  eventLabel: 'retry video',
                  eVar21: '{{storeValues:appData.msisdn}}',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop8: 'reverification',
                  prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
                  prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
                  prop5: '{{storeValues:outstationContext.isOutstation}}',
               },
            });
         }
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: "new_acq_postpaid_skyc",
               section: "stepsSection",
               subSection: "Step5Widget",
               eVar33: "{{storeValues:pincodeContext.pincodeDetails.pincode}}",
               eVar21: "{{storeValues:appData.msisdn}}",
               eVar20: "{{storeValues:appData.loggedInNumber}}",
               city: "{{storeValues:pincodeContext.pincodeDetails.city}}",
               customValue: "",
               eVar36: "{{storeValues:trackOrderContext.selectedMsisdn}}",
               eVar25: "{{storeValues:pincodeContext.pincodeDetails.circleId}}",
               isInteractive: 1,
               cartId: "{{storeValues:orderData.cartItemId}}",
               eventAction: "click",
               eventLabel: "retry",
               prop5: "{{storeValues:planInfo.planName}}",
               prop4: "SKYC",
               eventValue: "video verification",
               eVar0: "skyc_click on retry_video verification",
               customLabel: "button",
               prop45: "{{storeValues:appData.orderType}}",
               ticketId: "{{storeValues:orderData.cartId}}",
               prop26: ""
            },
         });
      }
      artistRef?.current?.onUserAction({
         actions,
      });
   };

   const onPressSubmit = () => {
      if (!videoData) return;

      // ✅ Safety net: block obviously-broken tiny blobs (blank videos are usually < 20KB)
      if (videoData.size < 20 * 1024) {
         artistRef?.current?.onUserAction({
            actions: [
               {
                  type: 'showToast',
                  payload: {
                     title: 'Recording failed. Please retry.',
                     dismissDuration: 5000,
                     position: 'bottom-center',
                     state: 'error',
                     style: { zIndex: 10000 },
                  },
               },
            ],
         });
         resetVideoData();
         return;
      }
      
      let actions = [];

      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const journeyType = storeValues?.appData?.lob;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      const circle = storeValues?.entityDetails?.circleId;
      let apiUrl = 'skyc/media-service/api/video/upload-video';
      const isPrepaidSimLayout = storeValues?.journeyContext?.layout === 'prepaid_sim_layout'

      let headers = {};
      let body = new FormData();

      if (isNewSKYCJourney) {
         apiUrl = 'new-acq/dochub/doc-hub/upload/video';
         const kycId = storeValues?.skycContext?.kycId;
         const consumerLob = storeValues?.appData?.lob;
         const circleId = storeValues?.pincodeContext?.pincodeDetails?.circleId;
         
         headers = {
            source: 'shop',
            'x-correlation-id': generateUniqueId(),
            'x-consumer-lob': consumerLob,
             circleId
         };
         body.append('docFile', videoData, 'record.mp4');
         body.append('docType', 'USER_LIVE_VIDEO');
         body.append('latitude', storeValues?.userLocation?.latitude);
         body.append('longitude', storeValues?.userLocation?.longitude);
         body.append('kycId', kycId);
         body.append('circle', circle);
         body.append('kycType', 'SKYC');
         body.append('channel', 'SHOP');
         body.append('journeyType', journeyType);
         body.append('requestTime', new Date().toISOString());
         if (!isPrepaidSimLayout) {
            actions?.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                  section: 'stepsSection',
                  subSection: 'Step5Widget',
                  eventAction: 'click',
                  eventLabel: 'submit',
                  eventValue: '',
                  customValue: '',
                  customLabel: 'button',
                  prop35: '{{storeValues:orderData.orderData.cartId}}',
                  extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                  prop45: '{{storeValues:appData.subJourney}}',
                  eVar21: '{{storeValues:appData.loggedInNumber}}',
                  eVar36: '{{storeValues:appData.msisdn}}',
                  city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                  eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                  prop4: 'SKYC',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop7: '{{storeValues:outstationContext.isOutstation}}',
                  prop9: '{{storeValues:appData.lob}}'
               },
            });
         }
      } else {
         const interactionId = getInteractionId(getReduxState,artistRef?.current?.onUserAction);

         body.append('videoValue', videoData, 'record.mp4');
         body.append('interactionId', interactionId);
         body.append('latitude', storeValues?.userLocation?.latitude);
         body.append('longitude', storeValues?.userLocation?.longitude);
         body.append('lob', 'CUSTOMER_REVERIFICATION');
         body.append('videoType', 'video');
         body.append('source', 'SHOP_RV');
         body.append('documentType', 'video');
         body.append('journey', 'skyc');
         body.append('retry', 'false');
         if(!isPrepaidSimLayout){
            actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE',
               section: 'stepssection',
               subSection: 'Step5Widget',
               eventAction: 'click',
               eventLabel: 'submit video',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
         }
      }

      if (isUploaded && isUploaded.current) {
         actions.push({
            payload: {
               args: ['closeWithoutUpload'],
               utilName: 'closeVideoModal',
            },
            type: 'callUtilFunction',
         });
      } else {
         actions.push({
            type: 'fullPageLoader',
            payload: {
               show: true,
            },
         });
         actions.push({
            type: 'apiCall',
            payload: {
               requestType: 'POST',
               apiUrl,
               authTokenGroup: 'SKYC_REVERIFICATION',
               ...{ headers },
               body,
               onSuccess: {
                  actions: [
                     {
                        type: 'fullPageLoader',
                        payload: {
                           show: false,
                        },
                     },
                     {
                        payload: {
                           args: ['success'],
                           utilName: 'closeVideoModal',
                        },
                        type: 'callUtilFunction',
                     },
                  ],
               },
               onError: {
                  actions: [
                     {
                        type: 'fullPageLoader',
                        payload: {
                           show: false,
                        },
                     },
                     {
                        type: 'showToast',
                        payload: {
                           title: 'something went wrong please try again',
                           dismissDuration: 5000,
                           position: 'bottom-center',
                           state: 'error',
                           style: {
                              zIndex: 10000,
                           },
                        },
                     },
                     ...(isNewSKYCJourney ? [
                        {
                           analyticsAction: 'impression',
                           analyticsData: {
                              eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                              section: 'stepsSection',
                              subSection: 'Step5Widget',
                              eventAction: 'impression',
                              eventLabel: 'submit',
                              eventValue: 'ecaf',
                              customValue: 'Video cannot be submitted',
                              customLabel: 'button',
                              prop35: '{{storeValues:orderData.orderData.cartId}}',
                              extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                              prop45: '{{storeValues:appData.subJourney}}',
                              eVar21: '{{storeValues:appData.loggedInNumber}}',
                              eVar36: '{{storeValues:appData.msisdn}}',
                              city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                              eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                              prop4: 'SKYC',
                              extraData11: '{{storeValues:layoutContext.layout}}',
                              prop7: '{{storeValues:outstationContext.isOutstation}}',
                              prop9: '{{storeValues:appData.lob}}',
                              eVar10: 'error'
                           },
                        }
                     ] : [])
                  ],
               },
            },
         });
      }
      if(isPrepaidSimLayout){
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: "new_acq_postpaid_skyc",
               section: "stepsSection",
               subSection: "Step5Widget",
               eVar33: "{{storeValues:pincodeContext.pincodeDetails.pincode}}",
               eVar21: "{{storeValues:appData.msisdn}}",
               eVar20: "{{storeValues:appData.loggedInNumber}}",
               city: "{{storeValues:pincodeContext.pincodeDetails.city}}",
               customValue: "",
               eVar36: "{{storeValues:trackOrderContext.selectedMsisdn}}",
               eVar25: "{{storeValues:pincodeContext.pincodeDetails.circleId}}",
               isInteractive: 1,
               cartId: "{{storeValues:orderData.cartItemId}}",
               eventAction: "click",
               eventLabel: "submit",
               prop5: "{{storeValues:planInfo.planName}}",
               prop4: "SKYC",
               eventValue: "video verification",
               eVar0: "skyc_click on submit_video verification",
               customLabel: "button",
               prop45: "{{storeValues:appData.orderType}}",
               ticketId: "{{storeValues:orderData.cartId}}",
               prop26: ""
            },
         });

         actions?.push({
            payload: {
               args: [
                  {
                     dataLayer: {
                        stepName: '5|video verification',
                        clicktext: '',
                        event: 'video_verification_submit',
                     },
                  },
               ],
               utilName: 'handleGtmDataLayerEvent',
            },
            type: 'callUtilFunction',
         });
      }
      artistRef?.current?.onUserAction({
         actions,
      });
   };

   const closeVideoModalWithError = () => {
      closeCameraModalWithErrorAction('video', artistRef);
   };

   const recordedVideoTime = useMemo(() => {
      const durationString = duration?.toString().padStart(2, '0');
      return `${durationString} SEC`;
   }, [duration]);

   return (
      <Modal visible={visible} animationType="slide" transparent={false}>
         <ModalCloseButton onPress={setIsVideoModalOpen} />
         <div className="video-outer-container">
            <div className="video-preview-container">
               {capturing && <VideoTimer duration={recordedVideoTime} />}
               {!previewVideo && (
                  <>
                     {loader && (
                        <Loader
                           bgColor="transparent"
                           theme="white"
                           size="xl"
                           color="rgba(256,256,256,0.9)"
                           style={{
                              position: 'absolute',
                              height: '100%',
                              width: '100%',
                              backgroundColor: 'rgba(0,0,0,0.9)',
                           }}
                        />
                     )}
                     <VideoCam
                        mirrored={true}
                        ref={webcamRef}
                        audioConstraints={true}
                        closeVideoModalWithError={closeVideoModalWithError}
                        onLoadedData={() => {
                           setLoader(false);
                        }}
                     />
                     {!loader && <CameraCircleOverlay />}

                     <div className="progress-container" style={{ zIndex: 999 }}>
                        <div
                           className="progress-fill"
                           style={{
                              width: `${videoProgress}%`,
                              backgroundColor: `${progressColor}`,
                              zIndex: 999,
                           }}
                        ></div>
                     </div>
                  </>
               )}
               {previewVideo && <VideoReplay src={videoURL} timer={recordedVideoTime} />}
            </div>

            <div className="video-btn-container">
               {!previewVideo ? (
                  <>
                     <Typography
                        type="body-para-sm-bold"
                        text="Position your face in the center and clearly say your full name and date of birth. Record for at least 5 seconds."
                        style={{
                           marginBottom: 8,
                        }}
                     />
                     <Callout
                        state="lilac"
                        description={
                           userDetails?.aadhaarName &&
                           userDetails?.dateOfBirth &&
                           `Read - I am ${userDetails?.aadhaarName}, born on ${userDetails?.dateOfBirth}.`
                        }
                        style={{
                           marginBottom: 20,
                           backgroundColor: `${BLUE_GREY_200}`,
                        }}
                     />
                     <VideoRecordingButton
                        disabled={loader}
                        active={!capturing}
                        onClick={() =>
                           !capturing ? handleVideoCapture() : handleStopCaptureClick()
                        }
                     />
                  </>
               ) : (
                  <ButtonWidget
                     direction="row"
                     leftCta={{
                        size: 'md',
                        buttonType: 'secondary',
                        title: 'Retry',
                        onPress: onPressRetry,
                     }}
                     rightCta={{
                        size: 'md',
                        buttonType: 'primary',
                        title: 'Submit',
                        onPress: onPressSubmit,
                     }}
                     style={{
                        width: '100%',
                     }}
                  />
               )}
            </div>
         </div>
      </Modal>
   );
};

export default VideoModal;